import React from "react";
import { Users } from "lucide-react";

/**
 * "Am I the kind of student they usually take?"
 *
 * Small horizontal-stacked-bar showing the KIS entry-qualifications
 * profile of *actual* accepted entrants (A-level / BTEC-etc. / Foundation
 * / Access / Degree-holders / Other), sourced from
 * `outcomes.entry_qualifications` populated by the KIS overlay.
 *
 * Non-UK degrees (or UK degrees where the KIS overlay hasn't run) render
 * nothing.
 *
 * Placement: directly below the 6-card triage grid, aligned with the
 * Admission-Probability card (top-left).
 */

// Order + labels — grouped so the bar reads left→right by qualification maturity
const _SLOTS = [
  { key: "alevel",  label: "A-level",       color: "bg-emerald-500" },
  { key: "bacc",    label: "IB / BAC",      color: "bg-emerald-400" },
  { key: "access",  label: "Access",        color: "bg-amber-400"   },
  { key: "foundtn", label: "Foundation",    color: "bg-amber-500"   },
  { key: "otherhe", label: "Other HE",      color: "bg-sky-400"     },
  { key: "degree",  label: "Degree-holder", color: "bg-sky-600"     },
  { key: "other",   label: "Other",         color: "bg-slate-400"   },
  { key: "noquals", label: "No formal",     color: "bg-slate-500"   },
];

export default function EntryProfileMini({ degree }) {
  const eq = degree?.outcomes?.entry_qualifications;
  if (!eq || !eq.sample_size) return null;

  // Keep only slots with a value > 0 so the bar isn't dominated by whitespace.
  const slots = _SLOTS.filter((s) => (eq[s.key] || 0) > 0);
  if (slots.length === 0) return null;

  const total = slots.reduce((acc, s) => acc + (eq[s.key] || 0), 0);

  return (
    <div
      data-testid="entry-profile-mini"
      className="mt-4 rounded-xl border border-stone-200 bg-white px-4 py-3"
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-500">
          <Users className="w-3 h-3" aria-hidden />
          <span>Who actually gets in</span>
        </div>
        <div className="text-[11px] text-stone-500">
          KIS · n=
          <span data-testid="entry-profile-sample" className="tabular-nums">
            {eq.sample_size}
          </span>
        </div>
      </div>

      {/* Stacked bar */}
      <div
        data-testid="entry-profile-bar"
        className="h-3 rounded-full overflow-hidden flex bg-stone-100"
        role="img"
        aria-label={`Entry qualifications: ${slots
          .map((s) => `${eq[s.key]}% ${s.label}`)
          .join(", ")}`}
      >
        {slots.map((s) => (
          <div
            key={s.key}
            data-testid={`entry-slot-${s.key}`}
            className={`${s.color} h-full`}
            style={{ width: `${((eq[s.key] || 0) / total) * 100}%` }}
            title={`${s.label}: ${eq[s.key]}%`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-stone-600">
        {slots.map((s) => (
          <div
            key={s.key}
            data-testid={`entry-legend-${s.key}`}
            className="flex items-center gap-1"
          >
            <span className={`inline-block w-2 h-2 rounded-sm ${s.color}`} />
            <span className="tabular-nums">{eq[s.key]}%</span>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      <p className="mt-2 text-[11px] text-stone-500 leading-relaxed">
        Distribution of qualifications actually accepted on this course
        (HESA · Discover Uni). Use this to sanity-check whether your
        qualification is common for entrants.
      </p>
    </div>
  );
}
